<template>
    <Dialog :visible="open" modal dismissableMask :closable="false" :draggable="false"
        contentClass="!p-0 overflow-hidden rounded-lg" :style="{ width: '65rem', maxWidth: '95vw' }"
        :pt="{ header: { class: '!p-0' } }" @update:visible="v => !v && $emit('cancel')">
        <template #header>
            <div
                class="flex items-center justify-between w-full p-3 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-yellow-800 dark:to-gray-900 rounded-t-lg">
                <div class="flex items-center gap-3">
                    <div
                        class="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 dark:bg-yellow-700 flex items-center justify-center">
                        <i class="fa-solid fa-triangle-exclamation text-white text-lg"></i>
                    </div>
                    <h4 class="text-xl font-bold">
                        {{ t('calendar.changeConfirm.title', { type: change?.type == 'drop' ? t('calendar.changeConfirm.move') : t('calendar.changeConfirm.resize') }) }}
                    </h4>
                </div>
                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    class="flex-shrink-0" @click="$emit('cancel')" />
            </div>
        </template>


        <!-- BODY -->
        <div class="px-4 pb-4" v-if="reservation && change">

            <!-- Reservation Info Section -->
            <div class="flex items-center gap-2">
                <i class="fa-solid fa-calendar-check text-lg"></i>
                <h4 class="font-bold text-xl">{{ t('calendar.changeConfirm.info') }}</h4>
            </div>
            <div class="rounded-xl border p-3 space-y-2">
                <!-- Title -->
                <div>
                    <p class="font-semibold mb-0.5">{{ t('common.title') }}</p>
                    <p class="text-lg font-semibold">{{ reservation.title }}</p>
                </div>

                <!-- Description -->
                <div v-if="reservation.description">
                    <p class="font-semibold mb-0.5">{{ t('common.description') }}</p>
                    <p class="whitespace-pre-wrap leading-relaxed">{{ reservation.description
                        }}</p>
                </div>

                <Divider class="my-2" />

                <!-- Resource -->
                <div class="flex items-start gap-2">
                    <div class="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <i class="fa-regular fa-bookmark text-white"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="font-semibold mb-0.5">{{ t('calendar.changeConfirm.resource') }}</p>
                        <p class="truncate">{{ resourceName }}</p>
                    </div>
                </div>

                <!-- Visitor & Attendees Grid -->
                <div class="grid grid-cols-2 gap-3">
                    <div class="flex items-start gap-2">
                        <div class="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                            <i class="fa-regular fa-user text-white"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-semibold mb-0.5">{{ t('calendar.changeConfirm.visitor') }}</p>
                            <p class="truncate">
                                {{ reservation.visitors?.length > 0
                                    ? `${reservation.visitors[0].firstName} ${reservation.visitors[0].lastName}`
                                    : t('calendar.changeConfirm.noVisitor') }}
                            </p>
                        </div>
                    </div>

                    <div class="flex items-start gap-2">
                        <div class="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
                            <i class="fa-solid fa-users text-white"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-semibold mb-0.5">{{ t('calendar.changeConfirm.attendees') }}</p>
                            <p class="truncate">{{ reservation.attendees }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Before/After Comparison -->
            <div class="space-y-2 mt-4">
                <h4 class="font-bold uppercase tracking-wide flex items-center gap-2">
                    <i class="fa-regular fa-clock"></i>
                    <span>{{ t('calendar.changeConfirm.timeSlot') }}</span>
                </h4>

                <div class="flex items-start justify-between gap-3">
                    <!-- Old Slot -->
                    <div
                        class="flex-1 rounded-xl border-2 border-surface-300 bg-white dark:bg-gray-800 p-3 relative overflow-hidden">
                        <div class="absolute top-0 left-0 w-1 h-full bg-surface-400"></div>
                        <div class="flex items-start gap-2">
                            <div
                                class="w-9 h-9 rounded-full bg-surface-400 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                <i class="fa-regular fa-calendar-xmark text-white"></i>
                            </div>
                            <div class="flex-1">
                                <p class="font-bold uppercase tracking-wider mb-1.5">{{ t('calendar.changeConfirm.oldSlot') }}
                                </p>
                                <div class="space-y-1">
                                    <div class="flex items-center gap-2">
                                        <i class="fa-regular fa-calendar"></i>
                                        <span class="font-medium">{{ formatResourceDate(change.oldStart) }}</span>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <i class="fa-regular fa-clock"></i>
                                        <span>{{ formatResourceTimeRange(change.oldStart, change.oldEnd)
                                            }}</span>
                                    </div>
                                    <!-- The operator's OWN clock, beneath the space's. Rendered only when the two
                                         zones diverge at this instant; omitted entirely otherwise (D-06). The label
                                         lives in a tooltip because the card is too narrow for a visible one, so the
                                         icon keeps an accessible name of its own. -->
                                    <div v-if="showBrowserLocal(change.oldStart)" class="flex items-center gap-2 text-xs text-surface-400"
                                        v-tooltip.top="t('calendar.timezone.browserLocalTime')">
                                        <i class="fa-solid fa-globe" role="img"
                                            :aria-label="t('calendar.timezone.differsAria')"></i>
                                        <span>{{ formatBrowserRange(change.oldStart, change.oldEnd) }}</span>
                                    </div>
                                    <div class="flex items-center gap-2 mt-1">
                                        <i class="fa-solid fa-stopwatch"></i>
                                        <span>{{ t('calendar.changeConfirm.duration') }}: {{ calculateDuration(change.oldStart, change.oldEnd) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Arrow -->
                    <div class="flex items-center justify-center self-stretch px-1">
                        <div class="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                            <i class="fa-solid fa-arrow-right text-white"></i>
                        </div>
                    </div>

                    <!-- New Slot -->
                    <div
                        class="flex-1 rounded-xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-gray-900 p-3 relative overflow-hidden shadow-md">
                        <div class="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                        <div class="flex items-start gap-2">
                            <div
                                class="w-9 h-9 rounded-full bg-blue-500 dark:bg-blue-700 flex items-center justify-center flex-shrink-0">
                                <i class="fa-regular fa-calendar-plus text-white"></i>
                            </div>
                            <div class="flex-1">
                                <p class="font-bold text-blue-700 uppercase tracking-wider mb-1.5">{{ t('calendar.changeConfirm.newSlot') }}
                                </p>
                                <div class="space-y-1">
                                    <div class="flex items-center gap-2">
                                        <i class="fa-regular fa-calendar text-blue-600"></i>
                                        <span class="font-semibold">{{ formatResourceDate(change.newStart) }}</span>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <i class="fa-regular fa-clock text-blue-600"></i>
                                        <span class="font-medium">
                                            {{ formatResourceTimeRange(change.newStart, change.newEnd) }}
                                        </span>
                                    </div>
                                    <!-- Same secondary line as the old-slot card. Both ends of the comparison get the
                                         same treatment — inverting one and not the other would be worse than leaving
                                         both alone. -->
                                    <div v-if="showBrowserLocal(change.newStart)" class="flex items-center gap-2 text-xs text-surface-400"
                                        v-tooltip.top="t('calendar.timezone.browserLocalTime')">
                                        <i class="fa-solid fa-globe" role="img"
                                            :aria-label="t('calendar.timezone.differsAria')"></i>
                                        <span>{{ formatBrowserRange(change.newStart, change.newEnd) }}</span>
                                    </div>
                                    <div class="flex items-center gap-2 text-blue-700 mt-1">
                                        <i class="fa-solid fa-stopwatch"></i>
                                        <span>{{ t('calendar.changeConfirm.duration') }}: {{ calculateDuration(change.newStart, change.newEnd) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Duration Change Notice -->
                <div v-if="getDurationDifference(change.oldStart, change.oldEnd, change.newStart, change.newEnd)"
                    class="rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900 dark:border-amber-700 p-2 flex items-start gap-2">
                    <i class="fa-solid fa-circle-info text-amber-600 flex-shrink-0 mt-0.5"></i>
                    <div class="text-amber-800 dark:text-amber-200">
                        <span class="font-semibold">{{ t('calendar.changeConfirm.durationChange') }}:</span>
                        {{ getDurationDifference(change.oldStart, change.oldEnd, change.newStart, change.newEnd) }}
                    </div>
                </div>
            </div>

            <!-- Warning Notice -->
            <div class="rounded-xl p-3 flex items-start gap-2">
                <i class="fa-solid fa-triangle-exclamation text-amber-600 dark:text-amber-400 flex-shrink-0"></i>
                <p class="leading-relaxed">
                    {{ t('calendar.changeConfirm.warning') }}
                </p>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="p-8 text-center">
            <div class="w-20 h-20 rounded-full bg-surface-100 mx-auto flex items-center justify-center mb-3">
                <i class="fa-solid fa-xmark-circle text-5xl"></i>
            </div>
            <p class="">{{ t('calendar.changeConfirm.noChanges') }}</p>
        </div>

        <!-- FOOTER -->
        <div class="flex justify-end p-3 border-t bg-gray-50">
            <Button :label="t('calendar.changeConfirm.confirmAction')" icon="fa-solid fa-check" severity="success" class="!rounded-lg"
                @click="$emit('confirm')" />
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import type { Reservation } from "@/lib/types/reservation";
import { useResourcesStore } from "@/store/resource";
import { isForeignTimezoneAt, msToResourceLocalCalendarDate, msToResourceLocalTimeString } from "@/utils/date";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export type ReservationUpdate = {
    reservationId: string
    resourceId: string
    oldStart: number
    oldEnd: number
    newStart: number
    newEnd: number
    type: "drop" | "resize"
}

const { t, locale } = useI18n()
const resourcesStore = useResourcesStore()

const props = defineProps<{
    open: boolean
    reservation: Reservation | null
    change: ReservationUpdate | null
    resourceName: string
}>()

defineEmits<{
    (e: "confirm"): void
    (e: "cancel"): void
}>()

/**
 * The change's own space, resolved once. This modal confirms a drag gesture the operator just made on the grid, and
 * the grid draws each space on its OWN clock — so confirming in the browser's frame would show a different time from
 * the row that was just dragged. Both the before and the after value therefore render on the space's clock, with the
 * browser's own time demoted to a globe-marked secondary line where the two zones diverge.
 *
 * Both are a comparison: rendering one inverted and the other not would be worse than leaving both alone.
 *
 * `change.oldStart` / `newStart` are TRUE instants — the un-shift happens at CalendarView's emit boundary — so these
 * conversions are the same ones the hover card and the detail modal do, never a display instant read back.
 */
const changeTimezoneId = computed(() => (props.change ? resourcesStore.getResourceById(props.change.resourceId)?.timezone?.id ?? null : null))

function showBrowserLocal(startMs: number) {
    return isForeignTimezoneAt(changeTimezoneId.value, startMs)
}

/** THE PRIMARY date, on the space's clock. Same long format as the browser renderer below. */
function formatResourceDate(ts: number) {
    const day = msToResourceLocalCalendarDate(changeTimezoneId.value, ts) ?? new Date(ts)
    return day.toLocaleDateString(locale.value, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric"
    })
}

/** THE PRIMARY time range, on the space's clock. Same ` - ` separator as the browser renderer below. */
function formatResourceTimeRange(start: number, end: number) {
    const s = msToResourceLocalTimeString(changeTimezoneId.value, start)
    const e = msToResourceLocalTimeString(changeTimezoneId.value, end)
    return `${s} - ${e}`
}

/**
 * THE SECONDARY line: the operator's own clock. Carries a compact date as well as the range, because a far-offset
 * space can put the browser's reading on a different calendar day than the primary long date above it.
 * formatTimeRange below is unchanged and keeps producing today's exact string.
 */
function formatBrowserRange(start: number, end: number) {
    const day = new Date(start).toLocaleDateString(locale.value, { day: "2-digit", month: "short" })
    return `${day} ${formatTimeRange(start, end)}`
}

function formatTimeRange(start: number, end: number) {
    const s = new Date(start).toLocaleTimeString(locale.value, { hour: "2-digit", minute: "2-digit" })
    const e = new Date(end).toLocaleTimeString(locale.value, { hour: "2-digit", minute: "2-digit" })
    return `${s} - ${e}`
}

function calculateDuration(start: number, end: number) {
    const diff = end - start
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`
    } else if (hours > 0) {
        return `${hours}h`
    } else {
        return `${minutes}m`
    }
}

function getDurationDifference(oldStart: number, oldEnd: number, newStart: number, newEnd: number) {
    const oldDuration = oldEnd - oldStart
    const newDuration = newEnd - newStart
    const diff = newDuration - oldDuration

    if (diff === 0) return null

    const hours = Math.floor(Math.abs(diff) / (1000 * 60 * 60))
    const minutes = Math.floor((Math.abs(diff) % (1000 * 60 * 60)) / (1000 * 60))

    let durationStr = ""
    if (hours > 0 && minutes > 0) {
        durationStr = `${hours}h ${minutes}m`
    } else if (hours > 0) {
        durationStr = `${hours}h`
    } else {
        durationStr = `${minutes}m`
    }

    const typeStr = diff > 0 ? t('calendar.changeConfirm.longer') : t('calendar.changeConfirm.shorter')
    return `${diff > 0 ? '+' : '-'}${durationStr} ${typeStr}`
}
</script>
